import dateTimeHelper from '../datetimeHelper';

export default class TimesheetHelper {
  // Builds a local Date from an entry's date ('YYYY.MM.DD') and time ('HH:mm') strings.
  // Parsed part by part on purpose: dot-separated date strings are not parsed reliably by
  // every browser (the same reason dateTimeHelper.getDateFromDateStringForFF exists).
  static GetEntryDateTime(dateString, timeString) {
    if (!dateString) return null;
    const dateParts = dateString.toString().split(/[.\-/]/);
    if (dateParts.length < 3) return null;
    const timeParts = (timeString || '00:00').toString().split(':');

    return new Date(
      Number(dateParts[0]),
      Number(dateParts[1]) - 1,
      Number(dateParts[2]),
      Number(timeParts[0]),
      Number(timeParts[1]),
      0,
      0
    );
  }

  // The real elapsed minutes of an entry, spanning midnight and multiple days correctly.
  // An entry booked on a single date whose end time is before its start time is treated as
  // ending the next day, matching the wrap handling in GetSpentHoursForEntries. Never
  // returns a negative value: a total built from these can only ever be understated, not
  // flipped below zero.
  static GetEntryDurationInMinutes(entry) {
    if (!entry) return 0;
    const start = this.GetEntryDateTime(entry.startDate, entry.startTime);
    let end = this.GetEntryDateTime(entry.endDate || entry.startDate, entry.endTime);
    if (!start || !end || isNaN(start.getTime()) || isNaN(end.getTime())) return 0;

    if (end.getTime() < start.getTime() && entry.startDate === (entry.endDate || entry.startDate)) {
      end = this.dateAdd(end, 'day', 1);
    }

    const minutes = Math.round((end.getTime() - start.getTime()) / 60000);

    return minutes > 0 ? minutes : 0;
  }

  // The grid list holds an extra display row for every entry that crosses midnight (see
  // TimeSheetEntryService.ConvertDBTimesheetEntries): both rows share the entry id and keep
  // the full original span in startTime/endTime — only the display/temp fields are split —
  // so any total has to count each id once.
  static GetDistinctEntries(timesheetEntries) {
    const seenIds = new Set();

    return (timesheetEntries || []).filter((timesheetEntry) => {
      if (!timesheetEntry || !timesheetEntry.id) return true;
      if (seenIds.has(timesheetEntry.id)) return false;
      seenIds.add(timesheetEntry.id);

      return true;
    });
  }

  static GetSpentHoursForEntries(timesheetEntries) {
    for (const timesheetEntry of timesheetEntries) {
      // If the entry ends at midnight, and is a multi-day entry, we have to factor in the number of days
      if (timesheetEntry.startDate !== timesheetEntry.endDate && timesheetEntry.tempEndTime === '00:00') {
        // Calculate the entry hours for one day
        const startTimeInMinutes = dateTimeHelper.convertTimeToMinutes(timesheetEntry.startTime);
        const endTimeInMinutes = dateTimeHelper.convertTimeToMinutes(timesheetEntry.endTime);
        let spentHoursOnOneDay = (endTimeInMinutes - startTimeInMinutes) / 60;
        // Stay numeric until the very end: rounding to a string here made the midnight-wrap
        // correction below concatenate ('-16.00' + 24 = '-16.0024') instead of add, which is
        // what produced negative spent hours for entries ending at midnight.
        if (spentHoursOnOneDay < 0) spentHoursOnOneDay += 24;
        // Count the days between the start and end date
        const differenceInDays = this.daysBetween(
          this.GetEntryDateTime(timesheetEntry.tempStartDate, '00:00'),
          this.GetEntryDateTime(timesheetEntry.tempEndDate, '00:00')
        );

        // Set the spent hours to spent hours on a day multiply by number of days
        timesheetEntry.spentHours = (spentHoursOnOneDay * differenceInDays).toFixed(2);
      } else if (
        timesheetEntry.startDate !== timesheetEntry.endDate &&
        timesheetEntry.startTime > timesheetEntry.endTime
      ) {
        const startDate = new Date(timesheetEntry.startDate);
        const endDate = new Date(timesheetEntry.endDate);

        const startDateTime = new Date(
          startDate.getFullYear(),
          startDate.getMonth(),
          startDate.getDate(),
          timesheetEntry.startTime.split(':')[0],
          timesheetEntry.startTime.split(':')[1],
          0
        );
        const endDateTime = new Date(
          endDate.getFullYear(),
          endDate.getMonth(),
          endDate.getDate(),
          timesheetEntry.endTime.split(':')[0],
          timesheetEntry.endTime.split(':')[1],
          0
        );

        const difference = endDateTime - startDateTime;
        let spentHoursOnOneDay = difference / 36e5;
        spentHoursOnOneDay = spentHoursOnOneDay < 0 ? spentHoursOnOneDay + 24 : spentHoursOnOneDay;
        timesheetEntry.spentHours = spentHoursOnOneDay.toFixed(2);
      } else {
        const startTimeInMinutes = dateTimeHelper.convertTimeToMinutes(timesheetEntry.startTime);
        const endTimeInMinutes = dateTimeHelper.convertTimeToMinutes(timesheetEntry.endTime);
        let spentHoursOnOneDay = (endTimeInMinutes - startTimeInMinutes) / 60;
        spentHoursOnOneDay = spentHoursOnOneDay < 0 ? spentHoursOnOneDay + 24 : spentHoursOnOneDay;
        timesheetEntry.spentHours = spentHoursOnOneDay.toFixed(2);
      }
    }

    timesheetEntries.sort((a, b) => new Date(a.startDate) - new Date(b.startDate));

    return timesheetEntries;
  }

  static daysBetween(date1, date2) {
    const millisecondsPerDay = 24 * 60 * 60 * 1000;
    const diff = Math.abs(date2 - date1);
    return Math.round(diff / millisecondsPerDay);
  }

  static ConvertEntryDatesToUtc(entry) {
    const combinedStartDate = new Date(
      entry.startDate.split('.').join('-').concat(`T${entry.startTime}`)
    ).toISOString(); // ToIsoString converts date to UTC

    entry.startDate = combinedStartDate.substring(0, 10);
    entry.startTime = combinedStartDate.substring(11, 16);

    const combinedEndDate = new Date(entry.endDate.split('.').join('-').concat(`T${entry.endTime}`)).toISOString();

    entry.endDate = combinedEndDate.substring(0, 10);
    entry.endTime = combinedEndDate.substring(11, 16);

    return entry;
  }

  static ConvertEntryDatesToLocal(entry) {
    const localUTCOffsetInMinutes = new Date().getTimezoneOffset();

    // Combine startdate and time to one date object (date.month is zero based so we need to subtrack 1)
    let combinedStartDate = new Date(
      entry.startDate.split('.')[0],
      entry.startDate.split('.')[1] - 1,
      entry.startDate.split('.')[2],
      entry.startTime.split(':')[0],
      entry.startTime.split(':')[1],
      0,
      0
    );

    combinedStartDate = this.dateAdd(combinedStartDate, 'minute', -1 * localUTCOffsetInMinutes);

    entry.startDate = dateTimeHelper.getFormattedDate(combinedStartDate, '.');
    entry.startTime = (
      combinedStartDate.getHours().toString().length === 1
        ? '0'.concat(combinedStartDate.getHours())
        : combinedStartDate.getHours()
    )
      .toString()
      .concat(
        `:${
          combinedStartDate.getMinutes().toString().length === 1
            ? '0'.concat(combinedStartDate.getMinutes())
            : combinedStartDate.getMinutes()
        }`
      );

    let combinedEndDate = new Date(
      entry.endDate.split('.')[0],
      entry.endDate.split('.')[1] - 1,
      entry.endDate.split('.')[2],
      entry.endTime.split(':')[0],
      entry.endTime.split(':')[1],
      0,
      0
    );

    combinedEndDate = this.dateAdd(combinedEndDate, 'minute', -1 * localUTCOffsetInMinutes);

    entry.endDate = dateTimeHelper.getFormattedDate(combinedEndDate, '.');
    entry.endTime = (
      combinedEndDate.getHours().toString().length === 1
        ? '0'.concat(combinedEndDate.getHours())
        : combinedEndDate.getHours()
    )
      .toString()
      .concat(
        `:${
          combinedEndDate.getMinutes().toString().length === 1
            ? '0'.concat(combinedEndDate.getMinutes())
            : combinedEndDate.getMinutes()
        }`
      );

    return entry;
  }

  static dateAdd(date, interval, units) {
    if (!(date instanceof Date)) return undefined;
    let ret = new Date(date); // don't change original date
    const checkRollover = () => {
      if (ret.getDate() !== date.getDate()) ret.setDate(0);
    };
    switch (String(interval).toLowerCase()) {
      case 'year':
        ret.setFullYear(ret.getFullYear() + units);
        checkRollover();
        break;
      case 'quarter':
        ret.setMonth(ret.getMonth() + 3 * units);
        checkRollover();
        break;
      case 'month':
        ret.setMonth(ret.getMonth() + units);
        checkRollover();
        break;
      case 'week':
        ret.setDate(ret.getDate() + 7 * units);
        break;
      case 'day':
        ret.setDate(ret.getDate() + units);
        break;
      case 'hour':
        ret.setTime(ret.getTime() + units * 3600000);
        break;
      case 'minute':
        ret.setTime(ret.getTime() + units * 60000);
        break;
      case 'second':
        ret.setTime(ret.getTime() + units * 1000);
        break;
      default:
        ret = undefined;
        break;
    }
    return ret;
  }
}
